import React, { lazy, useEffect, useMemo, useState } from "react";

import type { JSX } from "react/jsx-runtime";
import { useGlobal } from "@/context/GlobalContext";
import { Navigate, useLocation, useNavigate } from "react-router-dom"; // add this
import DashboardLayout from "./layouts/DashboardLayout";
import Loading from "@/components/Shared/Loading";
import TechnologyCheck from "./components/TechnologyCheck";

const EnrollmentPortal = lazy(() => import("./pages/EnrollmentPortal"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const DocumentsPage = lazy(() => import("./pages/DocumentsPage"));
const AdmissionsInterviewPage = lazy(
    () => import("./pages/AdmissionsInterviewPage")
);
const ProgramEnrollmentPage = lazy(
    () => import("./pages/ProgramEnrollmentPage")
);
const Fees = lazy(() => import("./pages/Fees"));
const FinancialPage = lazy(() => import("./pages/FinancialPage"));
//const DemoPlayer = lazy(() => import("./demo/DemoPlayer"));
const HousingPage = lazy(() => import("./pages/HousingPage"));
const HighSchoolTranscript = lazy(() => import("./pages/HighSchoolTranscript"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const SetPasswordPage = lazy(() => import("./pages/SetPasswordPage"));
const TransferCredits = lazy(() => import("./pages/TransferCredits"));
const MilitaryAndVeteransPage = lazy(() => import("./pages/MilitaryAndVeteransPage"));

/**
 * Downloads the signed-in pages' code ahead of use. Navigation runs inside a React transition (BrowserRouter's
 * default), so the first visit to a lazy page kept the previous page on screen, with nothing showing progress,
 * until its chunk had downloaded; a click looked ignored. Called once the applicant is signed in (PrivateRoute).
 * The specifiers match the lazy() calls above, so those resolve from the module cache.
 */
let protectedPagesPreloaded = false;
export function preloadProtectedPages(): void
{
    if (protectedPagesPreloaded) return;
    protectedPagesPreloaded = true;
    void Promise.all([
        import("./pages/Dashboard"),
        import("./pages/ProfilePage"),
        import("./pages/DocumentsPage"),
        import("./pages/AdmissionsInterviewPage"),
        import("./pages/ProgramEnrollmentPage"),
        import("./pages/Fees"),
        import("./pages/FinancialPage"),
        import("./pages/HousingPage"),
        import("./pages/HighSchoolTranscript"),
        import("./pages/TransferCredits"),
        import("./pages/MilitaryAndVeteransPage"),
    ]).catch(() =>
    {
        // A failed preload only means that page loads on its first visit, as before; allow a later retry.
        protectedPagesPreloaded = false;
    });
}

interface AppRoute {
    path: string;
    element: JSX.Element;
    protected?: boolean;
}

// Lightweight fade wrapper for swapping child content
const FadeSwap: React.FC<{ children: JSX.Element; duration?: number; keyId?: string }> = ({ children, duration = 200, keyId }) => {
    const [opacity, setOpacity] = useState<number>(1);
    const [currentChild, setCurrentChild] = useState<JSX.Element>(children);

    useEffect(() => {
        setOpacity(0);
        const outT = window.setTimeout(() => {
            setCurrentChild(children);
            setOpacity(1);
        }, duration);
        return () => {
            window.clearTimeout(outT);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keyId]);

    const style = useMemo<React.CSSProperties>(() => ({
        transition: `opacity ${duration}ms ease-in-out`,
        opacity
    }), [opacity, duration]);

    return <div style={style}>{currentChild}</div>;
};

// Gate for "/" that skips EnrollmentPortal when authenticated
const RootGate: React.FC = () => {
    const { isAuthenticated, isLoading, isLoggedOut, isVerified, PreviewMode, getUser } = useGlobal();
    const user = getUser();
    const [hasNavigated, setHasNavigated] = useState<boolean>(false);
    const navigate = useNavigate();

    // Redirect authenticated-but-unverified users to the verify page.
    // Guard on !isLoading to avoid acting on stale ref values during initialisation.
    // Guard on !isLoggedOut to prevent the redirect firing immediately after logout
    // before the fresh getCurrentUser() call has had a chance to run.
    useEffect(() => {
        if (!isLoading && !isLoggedOut && isAuthenticated && !isVerified && !hasNavigated) {
            setHasNavigated(true);

            const email = user?.email || '';
            const verifyUrl = email
                ? `/Verify?view=verify&showEmailPopup=true&email=${encodeURIComponent(email)}`
                : '/Verify?view=verify&showEmailPopup=true';
            navigate(verifyUrl);
        }
    }, [isLoading, isLoggedOut, isAuthenticated, isVerified, hasNavigated, user?.email, navigate]);

    const content = PreviewMode
        ? <EnrollmentPortal />
        : (isAuthenticated && isVerified ? <Navigate to="/dashboard" replace /> : <EnrollmentPortal />);

    const fadeKey = `${PreviewMode ? "preview" : "live"}-${isAuthenticated ? "authed" : "guest"}`;

    return (
        <>
            <FadeSwap keyId={fadeKey} duration={1}>{content}</FadeSwap>
            <Loading message="Loading..." fullscreen visible={isLoading} />
        </>
    );
};

// Handles first visit redirects into the profile flow.
// NOTE: We intentionally do NOT force ?step=personal onto a bare /dashboard/profile
// URL. ProfilePage owns "continue from last panel" resume (via useFirstUncompletedStep);
// injecting an explicit step here would set hasExplicitStep=true and suppress that
// resume, pinning the user to the Personal panel even when earlier panels are complete.
const ProfileStepGuard: React.FC<{ children: JSX.Element }> = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { getUser, godMode } = useGlobal();
    const user = getUser();

    useEffect(() => {
        if (godMode) return;

        const allowedFirstVisitPages = [
            "/dashboard/financial",
            "/dashboard/documents",
            "/dashboard/program-enrollment",
        ];
        // Redirect first-visit users into the profile flow only when they are
        // NOT already on it. The profile page owns its own step navigation
        // (?step=...), which lives in location.search, not location.pathname.
        // Without this guard clause, every intra-profile step change re-fires
        // this effect and navigates back to the bare /dashboard/profile URL,
        // resetting the user to the first panel on every Continue click.
        // Safety net: never hijack the profile-completion hand-off to the dashboard
        // (/dashboard?profileComplete=true). Even if firstVisit is momentarily stale,
        // this lets the ProfileCompleteModal render instead of bouncing the now-complete
        // user back into the locked profile flow.
        const isProfileCompleteHandoff =
            location.pathname === "/dashboard" &&
            new URLSearchParams(location.search).get("profileComplete") === "true";

        if (
            user?.firstVisit &&
            !location.pathname.startsWith("/dashboard/profile") &&
            !allowedFirstVisitPages.includes(location.pathname) &&
            !isProfileCompleteHandoff
        ) {
            navigate("/dashboard/profile");
            return;
        }
    }, [location.pathname, location.search, navigate, user?.firstVisit, godMode]);

    return children;
};

// Simple Logout Page Component that performs logout
function LogoutPage() {
    const { logout, getIsAuthenticated } = useGlobal();
    const isAuthenticated = getIsAuthenticated();
    const [done, setDone] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                await logout();
            } finally {
                if (!cancelled) setDone(true);
            }
        })();
        return () => { cancelled = true; };
    }, [logout]);

    if (done && !isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    // The shared loader: the Bootstrap spinner classes used here before are not in this app's CSS.
    return <Loading message="Logging you out..." fullscreen />;
}

export const appRoutes: AppRoute[] = [
    {
        path: "/",
        protected: false,
        element: <RootGate />, // use the gate here
    },
    {
        path: "/login",
        protected: false,
        element: <EnrollmentPortal />,
    },
    {
        path: "/register",
        protected: false,
        element: <EnrollmentPortal />,
    },
    {
        path: "/verify",
        protected: false,
        element: <EnrollmentPortal />,
    },
    {
        path: "/verify-email",
        protected: false,
        element: <EnrollmentPortal />,
    },
    {
        path: "/logout",
        protected: false,
        element: <LogoutPage />,
    },
    {
        path: "/forgot-password",
        protected: false,
        element: <ForgotPasswordPage />,
    },
    {
        path: "/set-password",
        protected: false,
        element: <SetPasswordPage />,
    },
    {
        path: "/dashboard",
        protected: true,
        element: (<ProfileStepGuard>
            <DashboardLayout>
                <main id="mainContent">
                    <Dashboard />
                </main>
            </DashboardLayout>
              </ProfileStepGuard >
        ),
    },
    {
        path: "/dashboard/profile",
        protected: true,
        element: (<ProfileStepGuard>
            <DashboardLayout>
                <main id="mainContent">
                 
                        <ProfilePage />
                  
                </main>
            </DashboardLayout>
        </ProfileStepGuard>
        ),
    },
    {
        path: "/dashboard/documents",
        protected: true,
        element: (
            <DashboardLayout>
                <main id="mainContent">
                    <DocumentsPage />
                </main>
            </DashboardLayout>
        ),
    },
    {
        path: "/dashboard/admissions-interview",
        protected: true,
        element: (
            <DashboardLayout>
                <AdmissionsInterviewPage />
            </DashboardLayout>
        ),
    },
    {
        path: "/dashboard/fees",
        protected: true,
        element: (
            <DashboardLayout>
                <Fees />
            </DashboardLayout>
        ),
    },
    {
        path: "/dashboard/program-enrollment",
        protected: true,
        element: (
            <DashboardLayout>
                <main id="mainContent">
                    <ProgramEnrollmentPage />
                </main>
            </DashboardLayout>
        ),
    },
    {
        path: "/dashboard/financial",
        protected: true,
        element: (
                <DashboardLayout>
                    <main id="mainContent">
                        <FinancialPage />
                    </main>
                </DashboardLayout>
        ),
    },
    {
        path: "/HousingPage",
        protected: true,
        element: (
            <DashboardLayout>
                <main id="mainContent">
                    <HousingPage />
                </main>
            </DashboardLayout>
        ),
    },
    {
        path: "/HighSchoolTranscript",
        protected: true,
        element: (
            <DashboardLayout>
                <main id="mainContent">
                    <HighSchoolTranscript />
                </main>
            </DashboardLayout>
        ),
    },
    {
        path: "/TransferCredits",
        protected: true,
        element: (
            <DashboardLayout>
                <main id="mainContent">
                    <TransferCredits />
                </main>
            </DashboardLayout>
        ),
    },
    {
        path: "/MilitaryAndVeterans",
        protected: true,
        element: (
            <DashboardLayout>
                <main id="mainContent">
                    <MilitaryAndVeteransPage />
                </main>
            </DashboardLayout>
        ),
    },
    {
        path: "/dashboard/TechnologyCheck",
        protected: true,
        element: (
            <DashboardLayout>
                <main id="mainContent">
                    <TechnologyCheck />
                </main>
            </DashboardLayout>
        ),
    },
];
